import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { ASSESSMENT_QUESTION_TYPE_LABELS, ASSESSMENT_QUESTION_TYPES, type QuestionView } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Checkbox } from '../../../components/Checkbox';
import { Drawer } from '../../../components/Drawer';
import { Field, FieldRow } from '../../../components/Field';
import { SearchInput } from '../../../components/Input';
import { Select } from '../../../components/Select';
import { SkeletonLines } from '../../../components/Skeleton';
import { EmptyState } from '../../../components/system-states/SystemState';
import { QUESTION_PAGE_SIZE, useQuestionFacets, useQuestions } from '../api/assessments-api';
import styles from './Assessments.module.css';

const MIN_QUERY = 2;
const ALL = '';

interface QuestionPickerProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly sectionName: string;
  /** Families already in this section, shown ticked and not added twice. */
  readonly chosenFamilyIds: ReadonlySet<string>;
  readonly ventureId: string;
  readonly onAdd: (questions: readonly QuestionView[]) => void;
  readonly onNewQuestion: () => void;
}

/** "Add questions" in the builder: the bank, searched and filtered, ticked in place. */
export function QuestionPicker(props: QuestionPickerProps): ReactNode {
  if (!props.isOpen) return null;
  return <Picker {...props} />;
}

function Picker({ isOpen, onClose, sectionName, chosenFamilyIds, ventureId, onAdd, onNewQuestion }: QuestionPickerProps): ReactNode {
  const [query, setQuery] = useState('');
  const [type, setType] = useState(ALL);
  const [tag, setTag] = useState(ALL);
  const [page, setPage] = useState(1);
  const [picked, setPicked] = useState<ReadonlyMap<string, QuestionView>>(new Map());
  const facets = useQuestionFacets();
  const typed = ASSESSMENT_QUESTION_TYPES.find((value) => value === type);
  const questions = useQuestions({
    page,
    ventureId,
    ...(query.trim().length >= MIN_QUERY ? { query: query.trim() } : {}),
    ...(typed ? { type: typed } : {}),
    ...(tag !== ALL ? { tag } : {}),
  });
  const rows = questions.data?.rows ?? [];
  const pageCount = Math.max(1, Math.ceil((questions.data?.total ?? 0) / QUESTION_PAGE_SIZE));

  function toggle(question: QuestionView, isOn: boolean): void {
    const next = new Map(picked);
    if (isOn) next.set(question.familyId, question);
    else next.delete(question.familyId);
    setPicked(next);
  }

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={`Add questions to ${sectionName || 'this section'}`}
      meta="Questions from this venture's bank"
      footer={
        <div className={styles.drawerFooter}>
          <Button variant="ghost" onClick={onNewQuestion}>
            Write a new question
          </Button>
          <Button variant="solid" disabled={picked.size === 0} onClick={() => onAdd([...picked.values()])}>
            {picked.size === 0 ? 'Add questions' : `Add ${picked.size} question${picked.size === 1 ? '' : 's'}`}
          </Button>
        </div>
      }
    >
      <div className={styles.form}>
        <SearchInput
          value={query}
          placeholder="Search the bank"
          onChange={(event) => {
            setQuery(event.target.value);
            setPage(1);
          }}
        />
        <FieldRow>
          <Field label="Kind of answer">
            {({ inputId }) => (
              <Select
                id={inputId}
                label="Kind of answer"
                value={type}
                options={[{ value: ALL, label: 'Any kind' }, ...ASSESSMENT_QUESTION_TYPES.map((value) => ({ value, label: ASSESSMENT_QUESTION_TYPE_LABELS[value] }))]}
                onChange={(value) => {
                  setType(value);
                  setPage(1);
                }}
              />
            )}
          </Field>
          <Field label="Skill">
            {({ inputId }) => (
              <Select
                id={inputId}
                label="Skill"
                value={tag}
                options={[{ value: ALL, label: 'Any skill' }, ...(facets.data?.tags ?? []).map((item) => ({ value: item, label: item }))]}
                onChange={(value) => {
                  setTag(value);
                  setPage(1);
                }}
              />
            )}
          </Field>
        </FieldRow>
        {questions.isPending ? (
          <SkeletonLines count={6} />
        ) : rows.length === 0 ? (
          <EmptyState
            isInset
            title="Nothing matches"
            body="Try another word or filter, or write a new question."
            action={
              <Button variant="outline" size="sm" onClick={onNewQuestion}>
                Write a new question
              </Button>
            }
          />
        ) : (
          <ul className={styles.pickList} aria-label="Questions in the bank">
            {rows.map((question) => {
              const isInSection = chosenFamilyIds.has(question.familyId);
              return (
                <li key={question.id} className={styles.pickItem}>
                  <Checkbox
                    checked={isInSection || picked.has(question.familyId)}
                    disabled={isInSection}
                    label={question.questionText}
                    onChange={(checked) => toggle(question, checked)}
                  />
                  <span className={styles.pickMeta}>
                    {ASSESSMENT_QUESTION_TYPE_LABELS[question.type]} · {question.points} mark{question.points === 1 ? '' : 's'}
                    {question.tags.length > 0 ? ` · ${question.tags.join(', ')}` : ''}
                    {isInSection ? ' · already in this section' : ''}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        {pageCount > 1 && (
          <nav className={styles.pager} aria-label="Pages">
            <Button variant="ghost" size="sm" icon={ChevronLeft} disabled={page <= 1} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <span className={styles.pageOf}>
              Page {page} of {pageCount}
            </span>
            <Button variant="ghost" size="sm" disabled={page >= pageCount} onClick={() => setPage(page + 1)}>
              Next
              <ChevronRight size={16} aria-hidden />
            </Button>
          </nav>
        )}
      </div>
    </Drawer>
  );
}
